import {
  CreateCustomerMarkupSchema,
  createCustomerMarkupSchema,
  CustomerDiscountConditionValueForm,
  CustomerMarkupConditionValueForm,
} from '@retail/customer-markup/components';
import { useFormContext } from 'react-hook-form';
import { useCustomerMarkupCreate } from '@retail/customer-markup/context';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { UniversalFormProvider } from '@shared/components';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useCallback, useState } from 'react';
import { PriceListAssortmentSelect } from './PriceListAssortmentSelect';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { CreateConditionDialog, ConditionTargetSelectContainer } from '../../ConditionActionsBase';
import {
  ConditionTarget,
  getConditionBindingValuesFromLevelSchema,
} from '@retail/calculus/components';
import { getISO_8601Date } from '@retail/utils';

export type CustomerMarkupConditionTarget =
  | { type: 'assortment' }
  | { type: 'supplier'; supplierNumber: number }
  | { type: 'chooseAssortmentOrSupplier' };

interface Props {
  /** What top level condition is bound to. 'chooseAssortmentOrSupplier' lets the user decide in the dialog. */
  target: CustomerMarkupConditionTarget;
  open: boolean;
  title?: string;
  valueMode?: 'markup' | 'discount';
  onClose: () => void;
}

function CreateCustomerMarkupConditionDialog(props: Props) {
  const t = useCalculusTFunction();
  const schema = createCustomerMarkupSchema(t);

  return (
    <UniversalFormProvider
      schema={schema}
      defaultValues={{
        ...schema.getDefault(),
        level: props.target.type === 'assortment' ? 'assortment' : '',
      }}
    >
      <CreateCustomerMarkupConditionForm {...props} />
    </UniversalFormProvider>
  );
}

function CreateCustomerMarkupConditionForm({
  target,
  open,
  title,
  valueMode = 'markup',
  onClose,
}: Props) {
  const t = useCalculusTFunction();
  const { isPriceListContext, selectedOrgUnitAssortment } = useSelectedOrgUnit();
  const {
    handleSubmit,
    reset,
    resetField,
    setValue,
    formState: { errors },
  } = useFormContext<CreateCustomerMarkupSchema>();

  const [selectedAssortmentIds, selectAssortmentId] = useState<number[]>(
    isPriceListContext ? [selectedOrgUnitAssortment.draftAssortmentId] : []
  );

  const isChoosingTarget = target.type === 'chooseAssortmentOrSupplier';
  const [chosenTarget, setChosenTarget] = useState<ConditionTarget | null>(null);
  const resolvedTarget = isChoosingTarget ? chosenTarget : target;
  const supplierNumber =
    resolvedTarget?.type === 'supplier' ? resolvedTarget.supplierNumber : undefined;

  const chooseTarget = useCallback(
    (newTarget: ConditionTarget | null) => {
      (
        ['item', 'overGroup', 'mainGroup', 'productGroup', 'discountGroups', 'gtin'] as const
      ).forEach((field) => resetField(field));
      setValue('level', newTarget?.type === 'assortment' ? 'assortment' : '');
      setChosenTarget(newTarget);
    },
    [resetField, setValue]
  );

  const { createCustomerMarkupCondition } = useCustomerMarkupCreate({
    assortmentIds: selectedAssortmentIds,
  });

  const onActivate = useCallback(
    (formValues: CreateCustomerMarkupSchema) => {
      const binding = getConditionBindingValuesFromLevelSchema({
        levelSchema: formValues,
        supplierNumber,
        gtinCode: formValues.gtin,
      })[0];

      createCustomerMarkupCondition({
        customerMarkup: {
          factor: formValues.enteredField === 'FACTOR' ? Number(formValues.factor) : undefined,
          percent: formValues.enteredField === 'PERCENT' ? Number(formValues.percent) : undefined,
        },
        binding,
        validFrom: getISO_8601Date(formValues.fromDate),
      });

      reset();
      onClose();
    },
    [supplierNumber, createCustomerMarkupCondition, reset, onClose]
  );

  return (
    <CreateConditionDialog
      open={open}
      title={title ?? t('calculus.createCustomerMarkup')}
      supplierNumber={supplierNumber}
      includePackages
      headerContent={
        isChoosingTarget && (
          <ConditionTargetSelectContainer value={chosenTarget} onChange={chooseTarget} />
        )
      }
      dialogContent={
        resolvedTarget && (
          <>
            {!isPriceListContext && (
              <PriceListAssortmentSelect
                selectedAssortmentIds={selectedAssortmentIds}
                selectAssortmentId={selectAssortmentId}
              />
            )}
            {valueMode === 'discount' ? (
              <CustomerDiscountConditionValueForm />
            ) : (
              <CustomerMarkupConditionValueForm />
            )}
          </>
        )
      }
      isDisabled={
        Object.keys(errors).length > 0 || selectedAssortmentIds.length === 0 || !resolvedTarget
      }
      onSubmit={handleSubmit(onActivate)}
      onClose={onClose}
    />
  );
}

export default withDialogSuspenseErrorBoundary(CreateCustomerMarkupConditionDialog);
